import constants from '../../../../shared/bk/modules/constants';

import { bulkTranslation, t } from '../../../../modules/bk/component-property-providers/bk-translations';
import AllocationRoleApi from '../am-allocation-role-api';
import bkDataTableInit from '../../../../custom-elements/bk/form-elements/bk-data-table/bk-data-table-init';
import { BKMSLogger } from 'Base/shared/bk/modules/bkmsLogger';
import { defineChangeVisibility } from 'Base/custom-elements/am/am-features/am-allocation-role-edit/utils';

const componentClassName = 'am-allocation-role-list';
const rolesListLocator = `${componentClassName}__roles_list`;
const rolesListIdColumnLocator = `${componentClassName}__roles_row_id`;
const rolesListStatusColumnLocator = `${componentClassName}__roles_row_status`;
const deleteButtonLocator = `${componentClassName}__delete_button`;
const modalLocator = `${componentClassName}__modal`;
const editButtonLocator = `${componentClassName}__edit_button`;
const createButtonLocator = `${componentClassName}__create_button`;

const { imagesUrl, contextPath } = constants;

const getTemplate = () => {
    const texts = bulkTranslation({
        editButtonLabel: 'global.edit',
        deleteButtonLabel: 'global.delete',
        newButtonLabel: 'clientAllocationRole.newAllocationRole',
        allocationRoleTitle: 'clientAllocationRole.title',
    });
    const template = document.createElement('template');
    template.innerHTML = `
    <h3>${texts.allocationRoleTitle}</h3>
    <div class="am-allocation-role-list__button-container">
      <img class="arrow" src="${imagesUrl}/pfeil-oben.gif" alt=""></td>
      <input type="button" class="${editButtonLocator}" name="editAllocationRole" value="${texts.editButtonLabel}" >
      <input type="button" class="${deleteButtonLocator}" name="deleteAllocationRole" value="${texts.deleteButtonLabel}">
      <input type="button" class="${createButtonLocator}" name="createAllocationRole" value="${texts.newButtonLabel}">
    </div>
    <bk-data-table class="${rolesListLocator}" data-users bk-id="rolesList"></bk-data-table>
    <bk-modal class="${modalLocator}"></bk-modal>
    `;

    return template;
};

const changeVisibility = defineChangeVisibility();

class AllocationRoleList extends HTMLElement {
    refs;
    #scope = AllocationRoleApi.SCOPE.RM;
    #language = 'eng';

    constructor() {
        super();
    }

    static get observedAttributes() {
        return ['scope'];
    }

    connectedCallback() {
        bkDataTableInit({ style: 'AM' });
        const mainTemplate = getTemplate().content;
        const main = document.importNode(mainTemplate, true);
        this.append(main);

        this.refs = {
            rolesList: this.querySelector(`.${rolesListLocator}`),
            deleteButton: this.querySelector(`.${deleteButtonLocator}`),
            editButton: this.querySelector(`.${editButtonLocator}`),
            createButton: this.querySelector(`.${createButtonLocator}`),
            modal: this.querySelector(`.${modalLocator}`),
        };

        changeVisibility(this.refs.editButton, false);
        changeVisibility(this.refs.deleteButton, false);

        this.refs.deleteButton.addEventListener('click', () => {
            const selectedInput = this.querySelector(`.${rolesListIdColumnLocator} input:checked`);
            const id = selectedInput?.dataset?.id;
            this.deleteRole(id);
        });

        this.refs.editButton.addEventListener('click', () => {
            const selectedInput = this.querySelector(`.${rolesListIdColumnLocator} input:checked`);
            const id = selectedInput?.dataset?.id;
            location = `${contextPath}app/sysadm/overview/allocation-role?&scope=${this.#scope}&id=${id}`;
        });
        this.refs.createButton.addEventListener('click', () => {
            location = `${contextPath}app/sysadm/overview/allocation-role?&scope=${this.#scope}`;
        });

        const csrf = document?.csrf;
        this.remote = new AllocationRoleApi({ csrf });

        const urlParams = new URLSearchParams(window?.location?.search);
        this.#language = urlParams.get('language') || 'eng';
        this.#scope = this.getAttribute('scope');

        this.getRoles();
    }

    deleteRole(id) {
        const t = bulkTranslation({
            deleteRoleTitle: 'clientAllocationRole.deleteAllocationRole',
            deleteRoleText: 'clientAllocationRole.deleteText',
            buttonDelete: 'global.delete',
        });

        this.refs.modal.openModal({
            isDialog: true,
            titleText: t.deleteRoleTitle,
            bodyContent: t.deleteRoleText,
            submitText: t.buttonDelete,
            submitFunction: async () => {
                try {
                    const csrf = document?.csrf;
                    await this.remote.deleteRole({ csrf, id });
                } catch (e) {
                    console.error(e);
                } finally {
                    this.refs.modal.closeModal();
                }
            },
            onCloseCallback: async () => {
                this.getRoles();
            },
        });
    }

    async getRoles() {
        try {
            const { rolesList } = this.refs;

            const roles = await this.remote.getRoles({ scope: this.#scope, language: this.#language });

            changeVisibility(this.refs.editButton, roles.length > 0);
            changeVisibility(this.refs.deleteButton, roles.length > 0);

            if (rolesList.isDataTable()) {
                rolesList.reload(roles);
            } else {
                const columns = [
                    { content: t('client.selection') },
                    { content: t('global.name') },
                    { content: t('global.comment') },
                    { content: t('global.status') },
                ];
                const render = (data, i, j, { row }) => {
                    const checked = row === 0 ? 'checked' : '';

                    return `<div class="${rolesListIdColumnLocator}">
                            <input type="radio" data-id="${data}" ${checked} name="allocationRoleId" size="10">
                        </div>`;
                };
                const statusRender = (data) => {
                    const text = data ? t('global.active.l') : t('global.inactive.l');

                    return `<div class="${rolesListStatusColumnLocator}">${text}</div>`;
                };

                const options = {
                    data: roles,

                    columns: [
                        { data: 'id', render, width: '24%', sortable: false },
                        { data: 'roleName', width: '32%', sortable: false },
                        { data: 'comment', width: '27%', sortable: false },
                        { data: 'isStatusActive', render: statusRender, width: '19%', sortable: false },
                    ],
                    sorting: false,
                    sortable: false,
                };
                rolesList.initDataTable(columns, options);
            }
        } catch (error) {
            this.errorHandler(error);
        }
    }

    errorHandler(error) {
        console.error(error);
        BKMSLogger.error(`am-allocation-role-list Error: ${error?.message || error}`);
    }
}

export default AllocationRoleList;
